import Close from "@mui/icons-material/Close";
import WarningAmber from "@mui/icons-material/WarningAmber";
import {
  Alert,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Typography,
} from "@mui/material";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate } from "react-router-dom";
import { useAppDispatch } from "@/app/store/hooks";
import { deleteChatSession } from "@/features/chat/model/chatHistorySlice";
import type { ChatSessionSummary } from "@/features/chat/model/chatHistoryTypes";
import { getChatSessionDisplayTitle } from "@/features/chat/model/filterChatSessions";

type DeleteChatConfirmDialogProps = {
  open: boolean;
  session: ChatSessionSummary | null;
  onClose: () => void;
};

export function DeleteChatConfirmDialog({ open, session, onClose }: DeleteChatConfirmDialogProps) {
  const { t } = useTranslation("chat");
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const [error, setError] = useState<string | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);

  useEffect(() => {
    if (!open) {
      setError(null);
      setIsDeleting(false);
    }
  }, [open]);

  function handleDialogClose(_event: object, reason?: "backdropClick" | "escapeKeyDown") {
    if (reason === "backdropClick" || isDeleting) {
      return;
    }
    onClose();
  }

  async function handleConfirmDelete() {
    if (!session) {
      return;
    }

    setError(null);
    setIsDeleting(true);

    try {
      await dispatch(deleteChatSession(session.sessionId)).unwrap();
      const activeSessionMatch = location.pathname.match(/^\/chat\/([^/]+)$/);
      if (activeSessionMatch?.[1] === session.sessionId) {
        navigate("/chat-history", { replace: true });
      }
      onClose();
    } catch (deleteError) {
      const message =
        typeof deleteError === "string"
          ? deleteError
          : t("history.deleteConfirm.errors.deleteFailed");
      setError(message);
    } finally {
      setIsDeleting(false);
    }
  }

  const displayTitle = session
    ? getChatSessionDisplayTitle(session, t("history.untitled"))
    : "";

  return (
    <Dialog
      open={open}
      onClose={handleDialogClose}
      fullWidth
      maxWidth="sm"
      slotProps={{
        paper: {
          sx: {
            bgcolor: "var(--theme-surface)",
            backgroundImage: "none",
          },
        },
      }}
    >
      <DialogTitle
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
        }}
      >
        <Typography component="span" variant="h6" sx={{ fontWeight: 700 }}>
          {t("history.deleteConfirm.title")}
        </Typography>
        <IconButton
          aria-label={t("history.deleteConfirm.closeAria")}
          onClick={onClose}
          disabled={isDeleting}
          edge="end"
        >
          <Close />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers>
        {error ? (
          <Alert severity="error" icon={<WarningAmber />} sx={{ mb: 2 }}>
            {error}
          </Alert>
        ) : null}
        <Typography variant="body1" sx={{ color: "var(--theme-text-primary)" }}>
          {t("history.deleteConfirm.message")}
        </Typography>
        {displayTitle ? (
          <Typography
            variant="body2"
            sx={{ mt: 1, color: "var(--theme-text-secondary)", fontWeight: 600 }}
          >
            {displayTitle}
          </Typography>
        ) : null}
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={onClose} disabled={isDeleting} sx={{ textTransform: "none" }}>
          {t("history.deleteConfirm.cancel")}
        </Button>
        <Button
          onClick={() => void handleConfirmDelete()}
          variant="contained"
          color="error"
          disabled={isDeleting}
          startIcon={isDeleting ? <CircularProgress size={16} color="inherit" /> : undefined}
          sx={{ textTransform: "none" }}
        >
          {t("history.deleteConfirm.confirm")}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
